import { MixpanelEvents } from "@/constants/mixpanel-events";
import {
  aiChatModalOpenAtom,
  chatMessagesAtom,
  chatOriginAtom,
  chatSessionIdAtom,
  mealPickerGreetingPendingAtom,
  pendingChatContextAtom,
} from "@/stores/chat.store";
import { clearChatWelcomePending } from "@/utils/chat-welcome-flag";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { useSetAtom, useStore } from "jotai";
import { useCallback } from "react";

export interface PrepareCoachChatOptions {
  /**
   * Skip the "resume a recent coach chat" rule and always start a blank chat.
   * Used by "Ask me something else..." on the Help screen, which the design
   * treats as an explicit request for a new conversation.
   */
  forceNew?: boolean;
}

/**
 * The "reset unless resumable" half of opening the coach chat, without opening
 * the chat window or tracking an open. Used directly by the chat page's Help
 * screen ("Ask me something else..."), where the chat is already open.
 */
export const usePrepareCoachChat = () => {
  // Read message/origin state imperatively at click time rather than
  // subscribing — the bottom bar is always mounted, and subscribing would
  // re-render it on every streamed token while the chat is open.
  const store = useStore();
  const setMessages = useSetAtom(chatMessagesAtom);
  const setSessionId = useSetAtom(chatSessionIdAtom);
  const setPendingContext = useSetAtom(pendingChatContextAtom);
  const setOrigin = useSetAtom(chatOriginAtom);
  const setMealPickerGreetingPending = useSetAtom(mealPickerGreetingPendingAtom);

  return useCallback(
    ({ forceNew = false }: PrepareCoachChatOptions = {}) => {
      const hasConversation = store.get(chatMessagesAtom).length > 0;
      const isResumableCoachChat = hasConversation && store.get(chatOriginAtom) === "coach";
      if (isResumableCoachChat && !forceNew) return;

      setSessionId(crypto.randomUUID());
      setMessages([]);
      setPendingContext(null);
      setOrigin("coach");
      setMealPickerGreetingPending(true);
      // Drop any still-pending post-onboarding welcome so the meal-picker
      // greeting always wins on a fresh open, matching its documented
      // priority over the welcome layout.
      clearChatWelcomePending();
    },
    [store, setMessages, setSessionId, setPendingContext, setOrigin, setMealPickerGreetingPending],
  );
};

/**
 * Opens the NutriCoach chat from the central bottom-bar Coach button.
 *
 * Resumes the cached transcript only when it is a general ("coach")
 * conversation still inside the cache window; anything seeded by a different
 * entry point (e.g. the My Plan meal coach, which opens with its own greeting
 * and meal context) is discarded so the central button always lands on the
 * "what would you like to eat next?" meal-picker greeting (Figma node
 * 273:1112) instead of the plain empty state. See `chatOriginAtom`.
 */
export const useOpenCoachChat = () => {
  const prepareCoachChat = usePrepareCoachChat();
  const openAiChatModal = useSetAtom(aiChatModalOpenAtom);

  return useCallback(() => {
    trackMixpanelEvent(MixpanelEvents.AI_CHAT_OPENED);
    prepareCoachChat();
    openAiChatModal(true);
  }, [prepareCoachChat, openAiChatModal]);
};

/**
 * Opens the NutriCoach chat on a brand-new conversation, discarding any cached
 * transcript regardless of origin or age. Used by "Ask me something else..."
 * on the standalone Help page (/help and Settings → Help), where the chat is
 * not yet open; the embedded Help screen inside the chat handles this itself
 * so it can first stop a reply that is still streaming.
 */
export const useOpenNewCoachChat = () => {
  const prepareCoachChat = usePrepareCoachChat();
  const openAiChatModal = useSetAtom(aiChatModalOpenAtom);

  return useCallback(() => {
    trackMixpanelEvent(MixpanelEvents.AI_CHAT_OPENED);
    prepareCoachChat({ forceNew: true });
    openAiChatModal(true);
  }, [prepareCoachChat, openAiChatModal]);
};
